import Head from 'next/head';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';

import { NextPageWithLayout } from './_app';
import React, { useMemo } from 'react';
import Layout from '@/index-layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { getPageContentContentApi } from '@/lib/service/home';
import { getTdkApi } from '@/lib/service/tdk';
import { page2JsonLd } from '@/lib/common/jsonld';
import { DynamicISRDebugPanel as ISRDebugPanel } from '@/components/ISRDebugPanel/dynamic';
import { ISR_REVALIDATE_TIME } from '@/lib/config/isr';

const DmcaTakedown = dynamic(() => import('@/components/dmca-takedown'), {
  ssr: true,
});

// ⭐ ISR: 转换为静态生成 + 7 天重新验证
export const getStaticProps: GetStaticProps = async (context) => {
  const { locale = 'en' } = context;
  const resolvedUrl = '/dmca-takedown';
  const tdkData = await getTdkApi(resolvedUrl, locale);
  const localeRes = await serverSideTranslations(locale, ['common']);
  let currentURL = `${process.env.NEXT_APP_WEB_HOST}/dmca-takedown`;
  if (locale != 'en') {
    currentURL = `${process.env.NEXT_APP_WEB_HOST}/${locale}/dmca-takedown`;
  }
  const res = await getPageContentContentApi(`/dmca-takedown`, locale, 0, '');
  // ⭐ ISR Fallback: 如果当前语言内容不存在，回退到英文
  if (res.code != 0) {
    // 尝试获取英文版本（默认语言）
    if (locale !== 'en') {
      const enRes = await getPageContentContentApi(`/dmca-takedown`, 'en', 0, '');
      if (enRes.code === 0 && enRes.data) {
        // 使用英文内容作为回退
        const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
        const siteUrl =
          locale !== 'en' ? `${process.env.NEXT_APP_WEB_HOST}/${locale}` : `${process.env.NEXT_APP_WEB_HOST}`;
        const pageTitle = tdkData?.data?.title ?? tempData['head_DmcaTakedown_Title'] ?? 'DMCA Takedown Service';
        const pageDescription =
          tdkData?.data?.description ?? tempData['head_DmcaTakedown_Desc'] ?? 'Professional DMCA takedown service';
        const pageKeywords = tdkData?.data?.keywords ?? tempData['head_DmcaTakedown_Keys'] ?? 'DMCA, takedown';

        const jsonLd = page2JsonLd(
          {
            title: pageTitle,
            desc: pageDescription,
            url: currentURL,
          },
          { url: siteUrl },
        );

        return {
          props: {
            ...localeRes,
            herf: currentURL,
            locale,
            data: enRes.data, // ✅ 使用英文内容
            title: pageTitle,
            description: pageDescription,
            keywords: pageKeywords,
            jsonLd: jsonLd,
            // ✅ 标记这是回退内容
            isFallbackContent: true,
            actualLocale: 'en',
            requestedLocale: locale,
          },
          revalidate: ISR_REVALIDATE_TIME,
        };
      }
    }

    // 如果英文版本也不存在，返回 notFound
    return {
      notFound: true,
      revalidate: ISR_REVALIDATE_TIME,
    };
  }
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `${process.env.NEXT_APP_WEB_HOST}/${locale}` : `${process.env.NEXT_APP_WEB_HOST}`;
  const pageTitle = tdkData?.data?.title ?? tempData['head_DmcaTakedown_Title'] ?? 'DMCA Takedown Service';
  const pageDescription =
    tdkData?.data?.description ?? tempData['head_DmcaTakedown_Desc'] ?? 'Professional DMCA takedown service';
  const pageKeywords = tdkData?.data?.keywords ?? tempData['head_DmcaTakedown_Keys'] ?? 'DMCA, takedown';

  const jsonLd = page2JsonLd(
    {
      title: pageTitle,
      desc: pageDescription,
      url: currentURL,
    },
    { url: siteUrl },
  );
  return {
    props: {
      ...localeRes,
      herf: currentURL,
      locale,
      data: res.data,
      title: pageTitle,
      description: pageDescription,
      keywords: pageKeywords,
      jsonLd: jsonLd,
      // ⭐ ISR 信息
      isrGeneratedAt: new Date().toISOString(),
      isrRevalidateIn: ISR_REVALIDATE_TIME,
    },
    revalidate: ISR_REVALIDATE_TIME,
  };
};

const Home: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props: any) => {
  return useMemo(
    () => (
      <>
        <ISRDebugPanel
          generatedAt={props.isrGeneratedAt}
          revalidateIn={props.isrRevalidateIn}
          pageName="DMCA Takedown"
          additionalInfo={{
            语言: props.locale,
          }}
        />
        <DmcaTakedown data={props} />
      </>
    ),
    [props],
  );
};
Home.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;
  const keywords = page?.props?.keywords;
  const jsonLd = page?.props?.jsonLd;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />
          <meta name="keywords" content={keywords} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta
            property="og:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta
            property="og:image:secure_url"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta
            name="twitter:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
          />
          <link rel="alternate" hrefLang="en" href={`${process.env.NEXT_APP_WEB_HOST}/en/dmca-takedown`} />
          <link rel="alternate" hrefLang="zh" href={`${process.env.NEXT_APP_WEB_HOST}/zh/dmca-takedown`} />
          <link rel="alternate" hrefLang="tw" href={`${process.env.NEXT_APP_WEB_HOST}/tw/dmca-takedown`} />
          <link rel="alternate" hrefLang="ja" href={`${process.env.NEXT_APP_WEB_HOST}/ja/dmca-takedown`} />
          <link rel="alternate" hrefLang="ko" href={`${process.env.NEXT_APP_WEB_HOST}/ko/dmca-takedown`} />
          <link rel="alternate" hrefLang="pt" href={`${process.env.NEXT_APP_WEB_HOST}/pt/dmca-takedown`} />
          <link rel="alternate" hrefLang="de" href={`${process.env.NEXT_APP_WEB_HOST}/de/dmca-takedown`} />
          <link rel="alternate" hrefLang="es" href={`${process.env.NEXT_APP_WEB_HOST}/es/dmca-takedown`} />
          <link rel="alternate" hrefLang="it" href={`${process.env.NEXT_APP_WEB_HOST}/it/dmca-takedown`} />
          <link rel="alternate" hrefLang="x-default" href={`${process.env.NEXT_APP_WEB_HOST}/dmca-takedown`} />

          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default Home;
